import { useState, useEffect } from 'react';
import { Calendar, ChevronDown, Filter, Loader, Search, UserRound, Users } from 'lucide-react';

const ReportsPage = () => {
  // State management
  const [activeTab, setActiveTab] = useState('client');
  const [dateFilter, setDateFilter] = useState('today');
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [reports, setReports] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [transactionTypeFilter, setTransactionTypeFilter] = useState('all');

  // Fetch data based on active tab and filters
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const endpoint = activeTab === 'client' 
          ? 'https://api.happychd.com/api/user' 
          : 'https://api.happychd.com/api/broker';
          
        const response = await fetch(endpoint);
        if (!response.ok) throw new Error('Failed to fetch data');
        
        const data = await response.json();
        
        // Process data to extract reports based on filters
        const filteredData = processAndFilterData(data);
        setReports(filteredData);
      } catch (error) {
        console.error('Error fetching data:', error);
        // In a real app, you would handle this error appropriately
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchData();
  }, [activeTab, dateFilter, paymentMethodFilter, transactionTypeFilter]);

  // Function to safely convert values to numbers
  const toNumber = (val) => Number(val || 0);

  // Function to compute data for each item
  const computeData = (user) => {
    const {
        commission = 0,
        idPercentage = 0,
        transactions = [],
        paymentStatus = [],
        balanceHistory = [],
    } = user;

    const totalDeposit = transactions
        .filter((t) => t.type === 'deposit')
        .reduce((sum, t) => sum + toNumber(t.amount), 0);

    const totalWithdraw = transactions
        .filter((t) => t.type === 'withdraw')
        .reduce((sum, t) => sum + toNumber(t.amount), 0);

    const totalPaymentStatus = paymentStatus.reduce((sum, p) => sum + toNumber(p.amount), 0);
    const totalBalance = balanceHistory.reduce((sum, b) => sum + toNumber(b.amount), 0);

    const rawIdSettled = (totalWithdraw + totalBalance - totalDeposit) * (idPercentage / 100);
    const cmsnSettled = rawIdSettled < 0 ? Math.abs(rawIdSettled * (commission / 100)) : 0;

    const idSettled = (rawIdSettled + cmsnSettled);
    const netSettlement = totalPaymentStatus + idSettled;

    return {
        totalDeposit,
      totalWithdraw,
      totalBalance,
      rawIdSettled,
      cmsnSettled,
      idSettled,
      netSettlement
    };
};

  // Function to process and filter data based on current filters
  const processAndFilterData = (data) => {
    // For demo purposes, we'll simulate the filtering logic
    // In a real app, this would properly filter based on dates and other criteria
    
    // Apply date filter
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    
    let filteredData = data.map(item => {
      // Extract transactions and payment status based on date filter
      let relevantTransactions = [];
      let relevantPaymentStatus = [];
      
      if (item.transactions) {
        relevantTransactions = item.transactions.filter(t => {
          const transactionDate = new Date(t.date);
          
          if (dateFilter === 'today') {
            return transactionDate >= today;
          } else if (dateFilter === 'last2days') {
            const twoDaysAgo = new Date(today);
            twoDaysAgo.setDate(today.getDate() - 2);
            return transactionDate >= twoDaysAgo;
          } else if (dateFilter === 'last7days') {
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(today.getDate() - 7);
            return transactionDate >= sevenDaysAgo;
          }
          return true;
        });
      }
      
      if (item.paymentStatus) {
        relevantPaymentStatus = item.paymentStatus.filter(p => {
          const paymentDate = new Date(p.date);
          
          if (dateFilter === 'today') {
            return paymentDate >= today;
          } else if (dateFilter === 'last2days') {
            const twoDaysAgo = new Date(today);
            twoDaysAgo.setDate(today.getDate() - 2);
            return paymentDate >= twoDaysAgo;
          } else if (dateFilter === 'last7days') {
            const sevenDaysAgo = new Date(today);
            sevenDaysAgo.setDate(today.getDate() - 7);
            return paymentDate >= sevenDaysAgo;
          }
          return true;
        });
      }
      
      // Apply payment method filter
      if (paymentMethodFilter !== 'all') {
        relevantTransactions = relevantTransactions.filter(t => 
          t.paymentMethod === paymentMethodFilter
        );
        
        relevantPaymentStatus = relevantPaymentStatus.filter(p => 
          p.paymentMethod === paymentMethodFilter
        );
      }
      
      // Apply transaction type filter
      if (transactionTypeFilter !== 'all' && relevantTransactions.length > 0) {
        relevantTransactions = relevantTransactions.filter(t => 
          t.type === transactionTypeFilter
        );
      }
      
      const enhancedItem = {
        ...item,
        filteredTransactions: relevantTransactions,
        filteredPaymentStatus: relevantPaymentStatus
      };
      
      // Compute additional data fields
      const calculatedData = computeData(enhancedItem);
      
      return {
        ...enhancedItem,
        ...calculatedData
      };
    });
    
    // Apply search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredData = filteredData.filter(item => 
        item.name?.toLowerCase().includes(query) || 
        item.userId?.toLowerCase().includes(query)
      );
    }
    
    // Add serial numbers
    filteredData = filteredData.map((item, index) => ({
      ...item,
      srNo: index + 1
    }));
    
    // Only show items that have transactions or payment status after filtering
    return filteredData.filter(item => 
      (item.filteredTransactions && item.filteredTransactions.length > 0) || 
      (item.filteredPaymentStatus && item.filteredPaymentStatus.length > 0)
    );
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Toggle filters visibility
  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-auto mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <h1 className="text-2xl font-bold text-gray-900">Reports Dashboard</h1>
            <div className="flex space-x-2">
              <button 
                onClick={toggleFilters}
                className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none"
              >
                <Filter className="h-4 w-4 mr-2" />
                Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="max-w-auto mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex border-b border-gray-200 mb-6">
          <button
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'client'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('client')}
          >
            <div className="flex items-center">
              <UserRound className="w-5 h-5 mr-2" />
              Clients
            </div>
          </button>
          <button
            className={`py-4 px-6 text-center border-b-2 font-medium text-sm ${
              activeTab === 'broker'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
            onClick={() => setActiveTab('broker')}
          >
            <div className="flex items-center">
              <Users className="w-5 h-5 mr-2" />
              Brokers
            </div>
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center justify-between space-y-4 md:space-y-0">
            <div className="flex-1 max-w-md">
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={`Search ${activeTab === 'client' ? 'clients' : 'brokers'} by name or ID...`}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <div className="flex items-center bg-white border border-gray-300 rounded-md">
                <Calendar className="h-5 w-5 text-gray-400 ml-3" />
                <select
                  className="block pl-2 pr-8 py-2 text-base border-0 focus:ring-0 focus:outline-none sm:text-sm rounded-md"
                  value={dateFilter}
                  onChange={(e) => setDateFilter(e.target.value)}
                >
                  <option value="today">Today</option>
                  <option value="last2days">Last 2 Days</option>
                  <option value="last7days">Last 7 Days</option>
                </select>
                <ChevronDown className="h-4 w-4 text-gray-400 mr-3" />
              </div>
            </div>
          </div>
        </div>

        {/* Advanced Filters - Collapsible */}
        {showFilters && (
          <div className="bg-white p-4 rounded-md shadow mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Payment Method</label>
              <select
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={paymentMethodFilter}
                onChange={(e) => setPaymentMethodFilter(e.target.value)}
              >
                <option value="all">All Methods</option>
                <option value="points">Points</option>
                <option value="upi">UPI</option>
                <option value="bank">Bank Transfer</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
              </select>
            </div>
            
            <div className="space-y-2">
              <label className="block text-sm font-medium text-gray-700">Transaction Type</label>
              <select
                className="w-full border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={transactionTypeFilter}
                onChange={(e) => setTransactionTypeFilter(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="deposit">Deposit</option>
                <option value="withdraw">Withdraw</option>
              </select>
            </div>
          </div>
        )}

        {/* Loading State */}
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader className="h-8 w-8 text-blue-500 animate-spin" />
            <span className="ml-2 text-gray-600">Loading reports...</span>
          </div>
        )}

        {/* Reports List */}
        {!isLoading && reports.length === 0 && (
          <div className="bg-white rounded-lg shadow p-6 text-center">
            <p className="text-gray-500">No reports found for the selected filters.</p>
          </div>
        )}

        {!isLoading && reports.length > 0 && (
          <div>
            {/* Main Summary Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden mb-6">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Summary Report</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sr.No.</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID %</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Commission %</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Deposit</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Withdraw</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Raw ID Settled</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cmsn Settled</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID Settled</th>
                      <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Net Settlement</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reports.map((report) => (
                      <tr key={report._id || report.userId} className="hover:bg-gray-50">
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{report.srNo}</td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{report.name}</div>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            report.netBalance >= 0 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {report.netBalance >= 0 ? 'Positive' : 'Negative'}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{report.userId}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{report.idPercentage}%</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{report.commission}%</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-green-600">{formatCurrency(report.totalDeposit)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-red-600">{formatCurrency(report.totalWithdraw)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                          <span className={report.totalBalance >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(report.totalBalance)}
                          </span>
                        </td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(report.rawIdSettled)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(report.cmsnSettled)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-500">{formatCurrency(report.idSettled)}</td>
                        <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                          <span className={report.netSettlement >= 0 ? 'text-green-600' : 'text-red-600'}>
                            {formatCurrency(report.netSettlement)}
                          </span>
                        </td>
                      </tr>
                    ))}
                    
                    {/* Total Row */}
                    <tr className="bg-gray-50 font-semibold">
                      <td className="px-3 py-4 whitespace-nowrap text-sm"></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm text-gray-900">TOTALS</td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm"></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm"></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm"></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm"></td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-green-600">
                        {formatCurrency(reports.reduce((sum, r) => sum + r.totalDeposit, 0))}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium text-red-600">
                        {formatCurrency(reports.reduce((sum, r) => sum + r.totalWithdraw, 0))}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                        <span className={reports.reduce((sum, r) => sum + r.totalBalance, 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(reports.reduce((sum, r) => sum + r.totalBalance, 0))}
                        </span>
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm">
                        {formatCurrency(reports.reduce((sum, r) => sum + r.rawIdSettled, 0))}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm">
                        {formatCurrency(reports.reduce((sum, r) => sum + r.cmsnSettled, 0))}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm">
                        {formatCurrency(reports.reduce((sum, r) => sum + r.idSettled, 0))}
                      </td>
                      <td className="px-3 py-4 whitespace-nowrap text-sm font-medium">
                        <span className={reports.reduce((sum, r) => sum + r.netSettlement, 0) >= 0 ? 'text-green-600' : 'text-red-600'}>
                          {formatCurrency(reports.reduce((sum, r) => sum + r.netSettlement, 0))}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* Detailed Reports */}
            <div className="space-y-6">
              {reports.map((report) => (
                <div key={`detail-${report._id || report.userId}`} className="bg-white rounded-lg shadow overflow-hidden">
                  {/* Report Header */}
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex flex-col md:flex-row md:justify-between md:items-center">
                      <div>
                        <div className="flex items-center">
                          <span className="bg-gray-200 text-gray-700 rounded-full w-8 h-8 flex items-center justify-center mr-2 text-sm font-medium">
                            {report.srNo}
                          </span>
                          <h3 className="text-lg font-medium text-gray-900">{report.name}</h3>
                        </div>
                        <p className="text-sm text-gray-500 mt-1">ID: {report.userId}</p>
                      </div>
                      <div className="mt-2 md:mt-0 grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div className="text-sm">
                          <span className="text-gray-500">ID %:</span>
                          <span className="ml-1 font-medium text-gray-900">{report.idPercentage}%</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">Commission:</span>
                          <span className="ml-1 font-medium text-gray-900">{report.commission}%</span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">Balance:</span>
                          <span className={`ml-1 font-medium ${report.totalBalance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(report.totalBalance)}
                          </span>
                        </div>
                        <div className="text-sm">
                          <span className="text-gray-500">Net Settlement:</span>
                          <span className={`ml-1 font-medium ${report.netSettlement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                            {formatCurrency(report.netSettlement)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Transactions */}
                  {report.filteredTransactions && report.filteredTransactions.length > 0 && (
                    <div className="px-6 py-4">
                      <h4 className="text-base font-medium text-gray-900 mb-3">Transactions</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead>
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200">
                            {report.filteredPaymentStatus.map((payment, idx) => (
                              <tr key={idx}>
                                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium">
                                  <span className={payment.amount >= 0 ? 'text-green-600' : 'text-red-600'}>
                                    {formatCurrency(payment.amount)}
                                  </span>
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500 capitalize">
                                  {payment.paymentMethod}
                                </td>
                                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                  {formatDate(payment.date)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  
                  {/* Settlement Details */}
                  <div className="px-6 py-4 border-t border-gray-200">
                    <h4 className="text-base font-medium text-gray-900 mb-3">Settlement Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="bg-gray-50 p-4 rounded-md">
                        <div className="text-sm text-gray-500">Raw ID Settled</div>
                        <div className="text-lg font-medium text-gray-900 mt-1">{formatCurrency(report.rawIdSettled)}</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <div className="text-sm text-gray-500">Commission Settled</div>
                        <div className="text-lg font-medium text-gray-900 mt-1">{formatCurrency(report.cmsnSettled)}</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <div className="text-sm text-gray-500">ID Settled</div>
                        <div className="text-lg font-medium text-gray-900 mt-1">{formatCurrency(report.idSettled)}</div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <div className="text-sm text-gray-500">Net Settlement</div>
                        <div className={`text-lg font-medium mt-1 ${report.netSettlement >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {formatCurrency(report.netSettlement)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;